import React, { useState, useEffect, useRef } from 'react';
import { Form, Dropdown, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser, faBolt, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'helpers/api';
import { debounce } from 'lodash';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  lead_type?: string;
  lead_source?: string;
  isNew?: boolean;
}

const GlobalLeadSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Add a test function to window for debugging - using the SAME API as LeadsAI.tsx
  useEffect(() => {
    (window as any).testLeadSearch = async (query: string = 'jon') => {
      console.log('🧪 [TEST] Testing with the SAME API call as LeadsAI.tsx:', query);
      
      try {
        // Use the EXACT same API call as the working leads page
        const response = await axiosInstance.get("/dashboard/leads", {
          params: query ? { search: query } : {}
        });

        // Backend now returns { success: true, data: { leads: [...], pagination: {...} } }
        const leadsData = response.data.data?.leads || response.data.leads || response.data.data || response.data || [];
        
        console.log(`✅ [TEST] Dashboard Leads Response:`, response.data);
        console.log(`✅ [TEST] Leads Array:`, leadsData);
        console.log(`✅ [TEST] Found ${leadsData.length} leads`);
        
        if (leadsData.length > 0) {
          console.log(`✅ [TEST] First Lead:`, leadsData[0]);
          console.log(`✅ [TEST] Field Structure:`, Object.keys(leadsData[0]));
        }
        
        return leadsData;
      } catch (error) {
        console.log(`❌ [TEST] API failed:`, error);
        return [];
      }
    };
    
    return () => {
      delete (window as any).testLeadSearch;
    };
  }, []);

  // Debounced search function - using the SAME approach as LeadsAI.tsx
  const debouncedSearch = debounce(async (query: string) => {
    if (query.length < 2) {
      setLeads([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔍 [SEARCH] Searching for:', query);
      
      // Use the EXACT same API call that works in LeadsAI.tsx
      const response = await axiosInstance.get("/dashboard/leads", {
        params: { search: query }
      });

      // Use the EXACT same response handling as LeadsAI.tsx
      // Backend now returns { success: true, data: { leads: [...], pagination: {...} } }
      const leadsData = response.data.data?.leads || response.data.leads || response.data.data || response.data || [];
      
      console.log("✅ [SEARCH] API Response:", response.data);
      console.log("✅ [SEARCH] Leads Data:", leadsData);
      
      // Debug: Log first lead to see actual structure
      if (leadsData.length > 0) {
        console.log("🔍 [SEARCH] First Lead Structure:", leadsData[0]);
        console.log("🔍 [SEARCH] Available Fields:", Object.keys(leadsData[0]));
      }
      
      // Use the EXACT same field mapping as LeadsAI.tsx
      const formattedLeads = leadsData
        .filter((lead: any) => {
          if (!lead) return false;
          
          // Use the actual field names from your database
          const firstName = lead.lead_first_name || '';
          const lastName = lead.lead_last_name || '';
          const fullName = `${firstName} ${lastName}`.trim();
          const email = lead.lead_email || '';
          const phone = lead.lead_phone || '';
          
          return fullName.length > 0; // Must have at least a name
        })
        .map((lead: any) => ({
          id: lead.id,
          name: `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim(),
          email: lead.lead_email,
          phone: lead.lead_phone,
          lead_type: lead.lead_type?.lead_type || 'Unknown',
          lead_source: lead.lead_source || 'Unknown',
          isNew: false
        }))
        .slice(0, 8); // Limit results for dropdown
      
      console.log('🔍 [SEARCH] Formatted leads:', formattedLeads);
      
      setLeads(formattedLeads);
      setShowDropdown(formattedLeads.length > 0);
    } catch (error) {
      console.error('❌ [SEARCH] Error searching leads:', error);
      setLeads([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLeadSelect = (lead: Lead) => {
    console.log('🔍 [SEARCH] Selected lead:', lead);
    setShowDropdown(false);
    setSearchTerm('');
    
    // Use the EXACT same navigation pattern as LeadsAI.tsx
    navigate(`/leads/${lead.id}?type=activity`);
  };

  const getLeadTypeColor = (leadType?: string) => {
    if (!leadType) return 'secondary';
    const type = leadType.toLowerCase();
    if (type.includes('buyer')) return 'primary';
    if (type.includes('seller')) return 'success';
    if (type.includes('investor')) return 'warning';
    return 'secondary';
  };

  return (
    <div 
      ref={searchRef}
      className="position-relative"
      style={{ width: '400px', maxWidth: '100%' }}
    >
      {/* AI-Enhanced Search Input */}
      <div
        className="d-flex align-items-center"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(209, 213, 219, 0.8)',
          borderRadius: '12px',
          padding: '12px 16px',
          transition: 'all 0.3s ease',
          boxShadow: showDropdown 
            ? '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.3)' 
            : '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}
        onFocus={() => {
          // Add focus styling through parent
        }}
      >
        <div 
          className="d-flex align-items-center justify-content-center me-3"
          style={{
            color: showDropdown ? '#3b82f6' : '#9ca3af',
            transition: 'color 0.3s ease'
          }}
        >
          {isLoading ? (
            <Spinner size="sm" style={{ color: '#3b82f6', width: '16px', height: '16px' }} />
          ) : (
            <FontAwesomeIcon 
              icon={faSearch} 
              style={{ 
                fontSize: '16px'
              }} 
            />
          )}
        </div>
        
        <Form.Control
          type="text"
          placeholder="Search customers & leads..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (leads.length > 0) setShowDropdown(true);
          }}
          className="border-0 bg-transparent"
          style={{
            color: '#374151',
            fontSize: '15px',
            fontWeight: 400,
            outline: 'none',
            boxShadow: 'none',
            padding: 0
          }}
        />

        <div 
          className="d-flex align-items-center"
          style={{
            background: 'rgba(34, 197, 94, 0.08)',
            padding: '6px 10px',
            borderRadius: '20px',
            border: '1px solid rgba(34, 197, 94, 0.2)'
          }}
        >
          <div 
            className="rounded-circle me-2"
            style={{
              width: '6px',
              height: '6px',
              background: '#22c55e',
              animation: 'pulse 2s infinite'
            }}
          />
          <span 
            className="fw-medium"
            style={{ 
              fontSize: '11px',
              color: '#15803d',
              textTransform: 'uppercase',
              letterSpacing: '0.3px'
            }}
          >
            AI
          </span>
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showDropdown && (
        <div
          className="position-absolute w-100 mt-2"
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(209, 213, 219, 0.8)',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
            zIndex: 9999,
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div 
            className="p-3 border-bottom"
            style={{ 
              borderColor: 'rgba(229, 231, 235, 0.8)',
              background: 'rgba(249, 250, 251, 0.5)'
            }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faBolt} 
                className="me-2"
                style={{ 
                  color: '#3b82f6',
                  fontSize: '16px'
                }}
              />
              <span 
                className="fw-medium"
                style={{ 
                  color: '#374151',
                  fontSize: '14px'
                }}
              >
                Search Results
              </span>
              {leads.length > 0 && (
                <Badge 
                  className="ms-2"
                  style={{ 
                    background: '#e5e7eb',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 500,
                    border: 'none'
                  }}
                >
                  {leads.length}
                </Badge>
              )}
            </div>
          </div>

          {/* Search Results */}
          {leads.length > 0 ? (
            <div className="p-2">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className="d-flex align-items-center p-3 mb-2 cursor-pointer"
                  style={{
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    background: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.06)';
                    e.currentTarget.style.transform = 'translateX(2px)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => handleLeadSelect(lead)}
                >
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={faUser} 
                      style={{ 
                        color: '#3b82f6',
                        fontSize: '14px'
                      }} 
                    />
                  </div>
                  
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h6 
                        className="mb-0 fw-medium me-2"
                        style={{ 
                          color: '#374151',
                          fontSize: '14px'
                        }}
                      >
                        {lead.name}
                      </h6>
                      {lead.lead_type && (
                        <Badge 
                          bg={getLeadTypeColor(lead.lead_type)}
                          style={{ fontSize: '10px' }}
                        >
                          {lead.lead_type}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="d-flex align-items-center text-muted">
                      {lead.email && (
                        <div className="d-flex align-items-center me-3">
                          <FontAwesomeIcon 
                            icon={faEnvelope} 
                            className="me-1"
                            style={{ fontSize: '10px' }}
                          />
                          <small style={{ fontSize: '12px' }}>{lead.email}</small>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon 
                            icon={faPhone} 
                            className="me-1"
                            style={{ fontSize: '10px' }}
                          />
                          <small style={{ fontSize: '12px' }}>{lead.phone}</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-muted">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="mb-2"
                style={{ fontSize: '24px', color: 'rgba(31, 41, 55, 0.3)' }}
              />
              <p className="mb-0" style={{ fontSize: '14px' }}>
                {searchTerm.length < 2 
                  ? 'Type at least 2 characters to search...' 
                  : 'No customers found matching your search.'
                }
              </p>
              {searchTerm.length >= 2 && (
                <div className="mt-3">
                  <small className="text-muted">
                    Using the same API as your main leads page: <code>/dashboard/leads</code>
                  </small>
                  <br />
                  <small className="text-muted">
                    Console test: <code>testLeadSearch("jon")</code>
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalLeadSearch; 